import React from 'react';
import { Button } from 'react-bootstrap';
import { ExclamationCircleFill } from 'react-bootstrap-icons';
import { FormattedMessage } from 'react-intl';
import OperatorAssignModeContent from './operator-assign-mode-content';
import { formatMessage } from '../../../translations/format-message';
import { CheckMarkIcon } from '../../../shared-components/svgs/svgs';

type InfoField = {
  labelKey: string;
  value: string | number | null | undefined;
  colWidth: number;
};

type ModeButton = {
  labelKey: string;
  className: string;
  onClick?: () => void;
  isDisabled: boolean;
};

type OperatorAssignSingleModeProps = {
  processName: string | null;
  handleBackClick: () => void;
  trayHistory: string[];
  selectedTrayId: string | null;
  shouldShowTrayWarning: (tray: string) => boolean;
  handleTraySelectChange: (tray: string) => void;
  showAddTrayInput: boolean;
  trayId: string;
  setTrayId: (value: string) => void;
  addTray: () => void;
  activeSize: string;
  handleFontSizeChange: (size: string) => void;
  infoFields: InfoField[];
  operatorResponseViewType: number;
  viewProps: any;
  isProcessStarted: boolean;
  operatorCount: number;
  handleDecrement: () => void;
  handleIncrement: () => void;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  formattedTime: string;
  buttons: ModeButton[];
  traySelectRef: React.RefObject<HTMLButtonElement>;
  shouldShowCheckMark: (tray: string) => boolean;
  showConfirmModal: boolean;
};

const OperatorAssignSingleMode = ({
  processName,
  handleBackClick,
  trayHistory,
  selectedTrayId,
  shouldShowTrayWarning,
  handleTraySelectChange,
  showAddTrayInput,
  trayId,
  setTrayId,
  addTray,
  activeSize,
  handleFontSizeChange,
  infoFields,
  operatorResponseViewType,
  viewProps,
  isProcessStarted,
  operatorCount,
  handleDecrement,
  handleIncrement,
  handleChange,
  formattedTime,
  buttons,
  traySelectRef,
  shouldShowCheckMark,
  showConfirmModal
}: OperatorAssignSingleModeProps) => {
  const hasTrays = trayHistory.length > 0;

  return (
    <>
      <div className="icon-nav navbar-fixed-top device-nav-bar userTopNav">
        <div className="row align-items-center">
          <div className="col-lg-5 col-md-6 col-sm-6 col-xl-3">
            <div className="page_title_wrap">
              <ul className="add-list operator-back">
                <li className="process-chart">
                  <Button
                    title={formatMessage('operator_list_detail')}
                    className="btn btn-secondary"
                    onClick={handleBackClick}
                  >
                    <FormattedMessage id="back" />
                  </Button>
                </li>
                <li>
                  <h1 className="page_title">{processName} </h1>
                </li>
              </ul>
            </div>
          </div>
          <div className="col-lg-7 col-md-6 col-sm-6 col-xl-9">
            <div className="tray-header">
              <div className="card-title d-flex align-items-center justify-content-end  mb-0">
                <div className="pe-4 d-flex scanned-tray">
                  <div>
                    <p className="text-white mt-2">
                      {formatMessage('scanned_trays')} : {trayHistory.length}
                    </p>
                  </div>
                  <div className="mx-2 operator-tray-select-wrap">
                    {hasTrays ? (
                      <div className="dropdown operator-tray-select">
                        <button
                          className="form-select dropdown-toggle"
                          type="button"
                          ref={traySelectRef}
                          data-bs-toggle="dropdown"
                          aria-expanded="false"
                        >
                          {selectedTrayId || formatMessage('tray_id')}
                          {selectedTrayId &&
                          shouldShowTrayWarning(selectedTrayId) && (
                            <ExclamationCircleFill className="text-danger ms-2" />
                          )}
                          { selectedTrayId && shouldShowCheckMark(selectedTrayId) && (
                            <CheckMarkIcon />
                          )}
                        </button>
                        <ul className="dropdown-menu">
                          <li>
                            <span className="dropdown-item disabled">
                              {formatMessage('tray_id')}
                            </span>
                          </li>
                          {trayHistory.map((tray) => (
                            <li key={tray}>
                              <button
                                type="button"
                                className={`dropdown-item d-flex justify-content-between align-items-center ${
                                  tray === selectedTrayId ? 'active' : ''
                                }`}
                                onClick={() => {
                                  if(tray === selectedTrayId) {
                                    return;
                                  }
                                  handleTraySelectChange(tray)}}
                              >
                                <span>{tray}</span>
                                {shouldShowTrayWarning(tray) && (
                                  <ExclamationCircleFill className="text-danger ms-2" />
                                )}
                                {shouldShowCheckMark(tray) && (
                                  <CheckMarkIcon />
                                )}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <button
                        className="form-select"
                        type="button"
                        ref={traySelectRef}
                        disabled
                      >
                        {formatMessage('tray_id')}
                      </button>
                    )}
                  </div>
                  {showAddTrayInput && (
                    <>
                      <div className="mx-2 operator-tray-input">
                        <input
                          className="form-control"
                          type="text"
                          placeholder={formatMessage('tray_id')}
                          onChange={(e) => setTrayId(e.target.value?.trim())}
                          value={trayId}
                        />
                      </div>
                      <div
                        className={`ms-1 ${trayId.length === 0 && 'cursor-not-allowed'}`}
                      >
                        <a
                          className={`btn btn-secondary ${trayId.length === 0 && 'disabled'}`}
                          data-toggle="modal"
                          onClick={() => {
                            if(trayId !== selectedTrayId && trayId.length > 0){
                              addTray();
                            }
                          }}
                          data-testid="modalbtn"
                          data-target="#addUser"
                        >
                          <svg
                            width="20"
                            height="20"
                            fill="currentColor"
                            className="bi bi-plus m-0"
                            viewBox="0 0 16 16"
                          >
                            <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z" />
                          </svg>
                          <FormattedMessage id={'add_tray'} />
                        </a>{' '}
                      </div>
                    </>
                  )}
                  <ul className="d-flex font-size-increase text-white justify-content-between">
                    <li
                      className={`font-reduce ${activeSize === 'small' ? 'active' : ''}`}
                      onClick={() => handleFontSizeChange('small')}
                    >
                      A-
                    </li>
                    <li
                      className={`font-normal ${activeSize === 'normal' ? 'active' : ''}`}
                      onClick={() => handleFontSizeChange('normal')}
                    >
                      A
                    </li>
                    <li
                      className={`font-increase ${activeSize === 'large' ? 'active' : ''}`}
                      onClick={() => handleFontSizeChange('large')}
                    >
                      A+
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="operator-block">
        <OperatorAssignModeContent
          infoFields={infoFields}
          trayHistory={trayHistory}
          operatorResponseViewType={operatorResponseViewType}
          viewProps={viewProps}
        />
        <div className="operator-footer-content ">
          <div className="row">
            <div className="col-auto">
              <div className="operator-timer d-flex">
                <label
                  className="badge bg-primary fw-medium"
                  htmlFor="flexSwitchCheckDefault"
                >
                  {formatMessage('single')}
                </label>
                <label className="form-check-label ms-3">
                  {formatMessage('operators')}
                </label>
                <div
                  className={`input-group operator ${isProcessStarted ? 'cursor-not-allowed' : ''}`}
                >
                  <button
                    className="btn btn-outline-light pause"
                    type="button"
                    onClick={handleDecrement}
                    disabled={isProcessStarted}
                  >
                    -
                  </button>
                  <input
                    type="number"
                    className="form-control text-center"
                    value={operatorCount > 0 ? operatorCount : 1}
                    onChange={handleChange}
                    min={1}
                    disabled={isProcessStarted}
                  />
                  <button
                    className="btn btn-outline-light"
                    type="button"
                    onClick={handleIncrement}
                    disabled={isProcessStarted}
                  >
                    +
                  </button>
                </div>
                <div className="timer d-flex align-items-center">
                  <div className="timer-data">
                    <p>{formattedTime}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="col">
              <div className="d-flex gap-4 flex-wrap justify-content-end">
                {buttons.map(
                  ({ labelKey, className, onClick, isDisabled }, index) => (
                    <div
                      className={`card-title ${isDisabled ? 'cursor-not-allowed' : ''}`}
                      key={index}
                    >
                      <button
                        className={`btn btn-lg ${className} ${isDisabled ? 'disabled' : ''}`}
                        onClick={onClick || undefined}
                        disabled={isDisabled}
                      >
                        {formatMessage(labelKey)}
                      </button>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OperatorAssignSingleMode;
